/**
 * trainerAvatar — the PLAYER and the NPC residents are REAL Pokemon-game TRAINERS.
 *
 * Unlike the PMD chibis / Pokemon avatars, each trainer is a single FRONT-FACING
 * static PNG ripped from Pokemon Showdown and vendored at setup to
 * /generated/trainers/<key>.png (see data/ingest/lib/trainers.ts). This module
 * resolves a trainer key to a Phaser texture and returns a small handle a scene can
 * place + animate.
 *
 * Loading mirrors SpriteResolver.tryLoad: load via a plain HTMLImageElement +
 * textures.addImage (NOT the scene LoaderPlugin) so concurrent loads (a whole town of
 * trainers at once) never race on a shared loader. It NEVER throws and never returns
 * null — a missing/failed sprite falls back to a neutral programmatic placeholder
 * texture so the world is always populated.
 *
 * Sprites are static, so "walking" is a gentle low-amplitude bob (calm by construction,
 * honoring the reduced-motion pillar), matching humanAvatar's contract.
 */
import Phaser from 'phaser';
import { trainerSpriteUrl } from './paths.js';

/** A live trainer avatar: the sprite plus a gentle walk-bob the scene can drive. */
export interface TrainerAvatar {
  /** The Phaser sprite (front-facing). Add to the scene/containers. */
  readonly sprite: Phaser.GameObjects.Sprite;
  /** Texture key the sprite uses (for pooling / sizing). */
  readonly textureKey: string;
  /** The resolved trainer key (may be the requested one, or the fallback). */
  readonly trainerKey: string;
  /** Start the gentle walk-bob (idempotent). Respects reduced-motion if passed true. */
  playWalk(reducedMotion?: boolean): void;
  /** Stop the walk-bob and settle to the resting pose. */
  stopWalk(): void;
}

/**
 * A curated set of player-pickable trainers (friendly labels, a male/female mix).
 * The new-game flow shows these; if a chosen key turns out to be missing at runtime
 * the loader falls back gracefully, but these are all in the vetted ingest list.
 */
export interface TrainerChoice {
  readonly key: string;
  readonly label: string;
  readonly gender: 'male' | 'female';
}

export const PLAYER_TRAINER_CHOICES: readonly TrainerChoice[] = [
  { key: 'red', label: 'Red', gender: 'male' },
  { key: 'lyra', label: 'Lyra', gender: 'female' },
  { key: 'ethan', label: 'Ethan', gender: 'male' },
  { key: 'may', label: 'May', gender: 'female' },
  { key: 'brendan', label: 'Brendan', gender: 'male' },
  { key: 'dawn', label: 'Dawn', gender: 'female' },
  { key: 'lucas', label: 'Lucas', gender: 'male' },
  { key: 'hilda', label: 'Hilda', gender: 'female' },
];

/** A sensible default when none is chosen (matches the v2→v3 male default). */
export const DEFAULT_TRAINER_KEY = 'red';

/** Trainer PNGs render roughly chibi-tall; match the PMD/human avatar footprint. */
const PLACEHOLDER_W = 32;
const PLACEHOLDER_H = 48;

/** Stable Phaser texture key for a resolved trainer sprite (content-pooled). */
export function trainerTextureKey(key: string): string {
  return `pr:trainer:${key}`;
}

/** The one neutral placeholder texture key (shared across every missing trainer). */
const PLACEHOLDER_KEY = 'pr:trainer:__placeholder__';

/**
 * Deterministically map a sim-trainer id to one of the available trainer keys, so the
 * same NPC always wears the same sprite across sessions and a busy town reads as a
 * varied crowd. Pure given (id, keys): a stable string hash modulo the key count.
 * Returns the default key when no keys are available.
 */
export function trainerKeyForSimTrainer(
  simTrainerId: string,
  availableKeys: readonly string[],
): string {
  if (availableKeys.length === 0) return DEFAULT_TRAINER_KEY;
  const sorted = [...availableKeys].sort((a, b) => a.localeCompare(b));
  const h = hashString(simTrainerId);
  return sorted[h % sorted.length]!;
}

/** A small, stable FNV-1a string hash (deterministic across runs). */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Ensure the neutral placeholder texture exists (drawn once, pooled). A soft, warm
 * silhouette so a missing trainer still reads as a friendly person, not a blank.
 */
function ensurePlaceholder(scene: Phaser.Scene): string {
  if (scene.textures.exists(PLACEHOLDER_KEY)) return PLACEHOLDER_KEY;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const cx = PLACEHOLDER_W / 2;
  // contact shadow
  g.fillStyle(0x000000, 0.18);
  g.fillEllipse(cx, PLACEHOLDER_H - 3, 18, 6);
  // body
  g.fillStyle(0x6c7a89, 1);
  g.fillRoundedRect(cx - 8, PLACEHOLDER_H - 28, 16, 26, 4);
  // head
  g.fillStyle(0xf2c8a0, 1);
  g.fillCircle(cx, PLACEHOLDER_H - 34, 8);
  // friendly face
  g.fillStyle(0x2a2a2a, 1);
  g.fillCircle(cx - 3, PLACEHOLDER_H - 35, 1.3);
  g.fillCircle(cx + 3, PLACEHOLDER_H - 35, 1.3);
  g.generateTexture(PLACEHOLDER_KEY, PLACEHOLDER_W, PLACEHOLDER_H);
  g.destroy();
  return PLACEHOLDER_KEY;
}

/**
 * Load one trainer sprite into a texture, race-free + never-throwing (mirrors
 * SpriteResolver.tryLoad). Resolves the texture key once decoded; resolves the
 * placeholder key on any 404 / decode error so the chain always terminates. If the
 * texture is already cached under its key, resolves immediately.
 */
export function ensureTrainerTexture(scene: Phaser.Scene, trainerKey: string): Promise<string> {
  const key = trainerTextureKey(trainerKey);
  if (scene.textures.exists(key)) return Promise.resolve(key);

  return new Promise<string>((resolve) => {
    const img = new Image();
    img.onload = (): void => {
      try {
        if (!scene.textures.exists(key)) scene.textures.addImage(key, img);
        resolve(scene.textures.exists(key) ? key : ensurePlaceholder(scene));
      } catch {
        resolve(ensurePlaceholder(scene));
      }
    };
    img.onerror = (): void => resolve(ensurePlaceholder(scene));
    img.src = trainerSpriteUrl(trainerKey);
  });
}

/**
 * Create a trainer overworld sprite for the player or an NPC. Returns SYNCHRONOUSLY
 * with the sprite already added (showing the placeholder), then swaps in the real
 * Showdown PNG as soon as it decodes — so a scene never has to await, and the world
 * is populated instantly even before sprites land. Origin is bottom-centre (0.5, 1)
 * so feet sit on the world position, matching the PMD chibis + humanAvatar.
 */
export function makePlayerTrainer(
  scene: Phaser.Scene,
  trainerKey: string,
  x = 0,
  y = 0,
): TrainerAvatar {
  const key = trainerKey || DEFAULT_TRAINER_KEY;
  // Start on the (always-present) placeholder so there is no flash of nothing.
  const placeholderKey = ensurePlaceholder(scene);
  const cachedKey = trainerTextureKey(key);
  const startKey = scene.textures.exists(cachedKey) ? cachedKey : placeholderKey;

  const sprite = scene.add.sprite(x, y, startKey);
  sprite.setOrigin(0.5, 1);

  // Swap to the real sprite once it decodes (no-op if already cached).
  if (startKey !== cachedKey) {
    void ensureTrainerTexture(scene, key).then((resolvedKey) => {
      // Guard: the sprite may have been destroyed by a scene shutdown mid-load.
      if (sprite.active && scene.textures.exists(resolvedKey)) sprite.setTexture(resolvedKey);
    });
  }

  let bob: Phaser.Tweens.Tween | null = null;

  return {
    sprite,
    textureKey: startKey,
    trainerKey: key,
    playWalk(reducedMotion = false): void {
      if (reducedMotion || bob) return;
      const base = sprite.y;
      bob = scene.tweens.add({
        targets: sprite,
        y: base - 2,
        duration: 320,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      });
    },
    stopWalk(): void {
      if (bob) {
        bob.stop();
        bob = null;
      }
    },
  };
}
